import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Card, Stack, Container, Typography, Button, Box } from "@mui/material";

import Page from "../components/Page";
import CitySupportFormComponent from "../components/Form/CitySupport";
import {
  fetchCitySupportList,
  createCitySupportProfile,
  updateCitySupportProfile
} from "src/utils/common";
import { notification } from "src/utils/messages";
import CustomLoader from "src/commonComponents/CustomLoader";

const defaultFormData = {
  support_title: "",
  city_id: 0,
  profile_image: "",
  name: "",
  designation: "",
  mobile: "",
  email: "",
  address: "",
  business_name: "",
  apartment_building: "",
  area: "",
  city: "",
  latitude: "",
  longitude: "",
  weekday_hours_from: "10:00",
  weekday_hours_to: "18:00",
  saturday_hours_from: "10:00",
  saturday_hours_to: "14:00",
  saturday_closed: false,
  sunday_status: "Closed",
  sunday_hours_from: "10:00",
  sunday_hours_to: "14:00"
};

export default function CitySupportForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEdit = Boolean(id);
  let loginUser = JSON.parse(localStorage.getItem("userData"));
  const isCityAdmin = loginUser?.u_type == 1;
  const preselectedCityIdRef = useRef(location.state?.preselectedCityId || 0);

  const [formdata, setFormdata] = useState(
    isCityAdmin
      ? { ...defaultFormData, city_id: loginUser.city_id }
      : preselectedCityIdRef.current
        ? { ...defaultFormData, city_id: preselectedCityIdRef.current }
        : defaultFormData
  );
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [profileImageFile, setProfileImageFile] = useState(null);

  useEffect(() => {
    if (isEdit) {
      loadProfile();
    }
  }, [id]);

  const parseTime = (t) => {
    if (!t) return "";
    const match = t.trim().match(/^(\d+):(\d+)\s*(AM|PM)?$/i);
    if (!match) return t;
    let hour = parseInt(match[1], 10);
    const min = match[2];
    const ampm = match[3]?.toUpperCase();
    if (ampm === "PM" && hour !== 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, "0")}:${min}`;
  };

  const loadProfile = async () => {
    setPageLoading(true);
    const cityIdToSend = isCityAdmin ? loginUser.city_id : 0;
    const list = await fetchCitySupportList(cityIdToSend);
    if (list) {
      const profile = list.find((item) => item.id == id);
      if (profile) {
        let hours = {};
        if (profile.weekday_hours) {
          const parts = profile.weekday_hours.split(" - ");
          hours.weekday_hours_from = parseTime(parts[0]) || "10:00";
          hours.weekday_hours_to = parseTime(parts[1]) || "18:00";
        }
        if (profile.saturday_hours) {
          if (profile.saturday_hours === "Closed") {
            hours.saturday_closed = true;
          } else {
            const parts = profile.saturday_hours.split(" - ");
            hours.saturday_hours_from = parseTime(parts[0]) || "10:00";
            hours.saturday_hours_to = parseTime(parts[1]) || "14:00";
          }
        }
        if (profile.sunday_status) {
          if (profile.sunday_status === "Closed") {
            hours.sunday_status = "Closed";
          } else {
            hours.sunday_status = "Custom";
            const parts = profile.sunday_status.split(" - ");
            hours.sunday_hours_from = parseTime(parts[0]) || "10:00";
            hours.sunday_hours_to = parseTime(parts[1]) || "14:00";
          }
        }
        const merged = { ...defaultFormData, ...profile, ...hours };
        if (!merged.city_id || merged.city_id == 0) {
          merged.city_id = isCityAdmin
            ? loginUser.city_id
            : preselectedCityIdRef.current;
        }
        setFormdata(merged);
      }
    }
    setPageLoading(false);
  };

  const validate = () => {
    const errs = {};
    if (!isCityAdmin && (!formdata.city_id || formdata.city_id == 0)) errs.city_id = "City is required";
    if (!formdata.name?.trim()) errs.name = "Name is required";
    if (!formdata.designation?.trim()) errs.designation = "Designation is required";
    if (!formdata.mobile?.trim()) errs.mobile = "Mobile number is required";
    else if (!/^\d{10}$/.test(formdata.mobile.trim())) errs.mobile = "Enter exactly 10 digits";
    if (!formdata.email?.trim()) errs.email = "Email is required";
    if (!formdata.address?.trim()) errs.address = "Address is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const serializeTime = (from, to, closed) => {
    if (closed) return "Closed";
    if (!from || !to) return "Closed";
    const fmt = (t) => {
      if (!t) return "";
      const [h, m] = t.split(":");
      const hour = parseInt(h);
      const ampm = hour >= 12 ? "PM" : "AM";
      const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${h12.toString().padStart(2, "0")}:${m} ${ampm}`;
    };
    return `${fmt(from)} - ${fmt(to)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const buildPayload = () => ({
      support_title: formdata.support_title || "",
      city_id: isCityAdmin ? loginUser.city_id : formdata.city_id,
      name: formdata.name,
      designation: formdata.designation,
      mobile: formdata.mobile,
      email: formdata.email,
      address: formdata.address,
      business_name: formdata.business_name || "",
      apartment_building: formdata.apartment_building || "",
      area: formdata.area || "",
      city: formdata.city || "",
      latitude: formdata.latitude || "",
      longitude: formdata.longitude || "",
      weekday_hours: serializeTime(
        formdata.weekday_hours_from,
        formdata.weekday_hours_to,
        false
      ),
      saturday_hours: serializeTime(
        formdata.saturday_hours_from,
        formdata.saturday_hours_to,
        formdata.saturday_closed
      ),
      sunday_status: formdata.sunday_status === "Custom"
        ? serializeTime(formdata.sunday_hours_from, formdata.sunday_hours_to, false)
        : "Closed"
    });

    let payload;
    if (profileImageFile) {
      payload = new FormData();
      const data = buildPayload();
      Object.entries(data).forEach(([key, val]) => payload.append(key, val));
      payload.append("profile_image", profileImageFile);
      if (isEdit) payload.append("id", parseInt(id));
    } else {
      payload = buildPayload();
      if (isEdit) payload.id = parseInt(id);
      if (formdata.profile_image) payload.profile_image = formdata.profile_image;
    }

    setIsLoading(true);
    const resp = isEdit
      ? await updateCitySupportProfile(payload)
      : await createCitySupportProfile(payload);
    setIsLoading(false);

    let notify = notification({
      type: resp?.status === 1 ? "success" : "error",
      message: resp?.message || "Something went wrong"
    });
    notify();

    if (resp?.status === 1) {
      navigate("/city-support");
    }
  };

  if (pageLoading) return <CustomLoader />;

  return (
    <Page title={isEdit ? "Edit Support Profile | LVB Privilege" : "Add Support Profile | LVB Privilege"}>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {isEdit ? "Edit Support Profile" : "Add Support Profile"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEdit
                ? "Update the public display and operational details"
                : "Configure a new helpline profile for your city"}
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => navigate("/city-support")}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            Back
          </Button>
        </Stack>

        <Card sx={{ p: 2.5 }}>
          <form onSubmit={handleSubmit}>
            <CitySupportFormComponent
              formdata={formdata}
              setFormData={setFormdata}
              errors={errors}
              setErrors={setErrors}
              isLoading={isLoading}
              profileImageFile={profileImageFile}
              setProfileImageFile={setProfileImageFile}
            />

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button variant="outlined" onClick={() => navigate("/city-support")}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : isEdit ? "Update Profile" : "Save Profile"}
              </Button>
            </Box>
          </form>
        </Card>
      </Container>
    </Page>
  );
}
